import { Copy, Check, Plus, Trash2, Power, RefreshCw, ExternalLink, Calendar, Hash, Bell, Layers } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isDataField } from '@/features/records/utils/fieldAccess'
import { isHiddenOnPublicForm } from '@/features/records/utils/prefill'
import { useAllForms, useFormById } from '@/hooks/useForm'
import { useFormPublicLinks } from '@/hooks/useFormPublicLinks'
import { useAllowedActions } from '@/hooks/usePermission'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { FormPublicLink, CreatePublicLinkRequest, FormBasicInfo } from '@/types/api/form'
import { FormPublicLinkStatus } from '@/types/enums'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/date'

interface ShareTabProps { formId: string; recordGroupId: string }

/**
 * Convert any ISO date string (UTC or with offset) back to the `yyyy-MM-ddTHH:mm`
 * format that `<input type="datetime-local">` expects as its `value`.
 */
function toDatetimeLocal(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso // already datetime-local format
    // Format as local time yyyy-MM-ddTHH:mm
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch {
    return iso
  }
}

const STATUS_CONFIG = {
  [FormPublicLinkStatus.ACTIVE]:    { label: 'Active',      cls: 'bg-success-100 text-success-700' },
  [FormPublicLinkStatus.INACTIVE]:  { label: 'Inactive',    cls: 'bg-muted text-muted-foreground' },
  [FormPublicLinkStatus.EXPIRED]:   { label: 'Expired',     cls: 'bg-amber-100 text-amber-700' },
  [FormPublicLinkStatus.MAX_REACHED]:{ label: 'Limit reached', cls: 'bg-rose-100 text-rose-700' },
}

function LinkCard({ link, allForms, onToggle, onDelete, onChangeVersion, isToggling, isDeleting, canManage, canDelete }: {
  link: FormPublicLink
  canManage: boolean
  canDelete: boolean
  allForms: FormBasicInfo[]
  onToggle: () => void
  onDelete: () => void
  onChangeVersion: (newFormId: string) => void
  isToggling: boolean
  isDeleting: boolean
}) {
  const [copied, setCopied] = useState(false)
  const publicUrl = `${window.location.origin}/forms/public/${link.token}`
  const status = STATUS_CONFIG[link.status] ?? STATUS_CONFIG[FormPublicLinkStatus.INACTIVE]

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success('Link copied!')
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">
              {link.title || 'Untitled Link'}
            </span>
            <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', status.cls)}>
              {status.label}
            </span>
          </div>
          {link.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{link.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {canManage && (
            <button onClick={onToggle} disabled={isToggling} title={link.active ? 'Deactivate' : 'Activate'}
              className="h-7 w-7 rounded-lg border border-border bg-card hover:bg-muted flex items-center justify-center transition-colors">
              {isToggling ? <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                : <Power className={cn('h-3.5 w-3.5', link.active ? 'text-success-600' : 'text-muted-foreground')} />}
            </button>
          )}
          {canDelete && (
            <button onClick={onDelete} disabled={isDeleting} title="Delete link"
              className="h-7 w-7 rounded-lg border border-destructive/20 bg-destructive/10 hover:bg-destructive/15 flex items-center justify-center transition-colors">
              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
            </button>
          )}
        </div>
      </div>

      {/* URL row */}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 rounded-lg border border-border bg-muted/40 px-3 py-1.5">
          <p className="text-xs font-mono text-muted-foreground truncate">{publicUrl}</p>
        </div>
        <button onClick={copyUrl}
          className="h-8 px-3 rounded-lg border border-border bg-card hover:bg-muted flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors shrink-0">
          {copied ? <Check className="h-3.5 w-3.5 text-success-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <a href={publicUrl} target="_blank" rel="noreferrer"
          className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted flex items-center justify-center transition-colors">
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
        </a>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1">
          <Layers className="h-3 w-3" />
          Form v{link.formVersion}
          {canManage && allForms.length > 1 && (
            <select
              value={link.formId}
              onChange={e => onChangeVersion(e.target.value)}
              className="ml-1 text-[11px] rounded border border-border px-1 py-0.5 bg-card text-foreground cursor-pointer hover:bg-accent"
              title="Change form version"
            >
              {allForms.map(f => (
                <option key={f.id} value={f.id}>
                  v{f.version}{f.id === link.formId ? ' (current)' : ''}
                </option>
              ))}
            </select>
          )}
        </span>
        <span className="flex items-center gap-1">
          <Hash className="h-3 w-3" />
          {link.submissionCount} submission{link.submissionCount !== 1 ? 's' : ''}
          {link.maxSubmissions != null && ` / ${link.maxSubmissions} max`}
        </span>
        {link.expiresAt && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Expires {formatDateTime(link.expiresAt)}
          </span>
        )}
        {link.notifyOnSubmissionCount != null && (
          <span className="flex items-center gap-1">
            <Bell className="h-3 w-3" />
            Email every {link.notifyOnSubmissionCount}
          </span>
        )}
      </div>
    </div>
  )
}

// Create Link Dialog

function CreateLinkDialog({ formId, open, onClose, onCreate, isCreating, allForms }: {
  formId: string; open: boolean; onClose: () => void
  onCreate: (data: CreatePublicLinkRequest) => void; isCreating: boolean
  allForms: FormBasicInfo[]
}) {
  const [form, setForm] = useState<Partial<CreatePublicLinkRequest>>({ formId })
  const set = (k: keyof CreatePublicLinkRequest, v: unknown) =>
    setForm(p => ({ ...p, [k]: v || undefined }))

  const handleCreate = () => {
    if (!form.formId) return
    // Convert browser datetime-local ("2026-06-25T16:31") to a full ISO string
    // that the backend's OffsetDateTime can always parse without ambiguity.
    const normalized: CreatePublicLinkRequest = {
      ...form as CreatePublicLinkRequest,
      expiresAt: form.expiresAt
        ? new Date(form.expiresAt as string).toISOString()
        : undefined,
    }
    onCreate(normalized)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Public Link</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Form version selector */}
          <div className="space-y-1">
            <Label>Form Version</Label>
            <Select
              value={form.formId ?? ''}
              onValueChange={(v) => set('formId', v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a version" />
              </SelectTrigger>
              <SelectContent>
                {allForms.map(f => (
                  <SelectItem key={f.id} value={f.id}>
                    Version {f.version}{f.id === formId ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Title <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input placeholder="e.g. Contact Form" value={form.title ?? ''} onChange={e => set('title', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input placeholder="Shown below the title on the form" value={form.description ?? ''} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Success message <span className="text-muted-foreground font-normal">(shown after submit)</span></Label>
            <Input placeholder="Thank you! We'll be in touch soon." value={form.successMessage ?? ''} onChange={e => set('successMessage', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Redirect URL <span className="text-muted-foreground font-normal">(overrides success message)</span></Label>
            <Input placeholder="https://your-site.com/thank-you" value={form.redirectUrl ?? ''} onChange={e => set('redirectUrl', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Expiry date</Label>
              {/* Store raw datetime-local string in state; convert to ISO on submit */}
              <Input
                type="datetime-local"
                value={form.expiresAt ? toDatetimeLocal(form.expiresAt as string) : ''}
                onChange={e => set('expiresAt', e.target.value || null)}
              />
            </div>
            <div className="space-y-1">
              <Label>Max submissions</Label>
              <Input type="number" min={1} placeholder="Unlimited" value={form.maxSubmissions ?? ''} onChange={e => set('maxSubmissions', e.target.value ? Number(e.target.value) : null)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Email me every N submissions</Label>
            <Input type="number" min={1} placeholder="e.g. 10" value={form.notifyOnSubmissionCount ?? ''} onChange={e => set('notifyOnSubmissionCount', e.target.value ? Number(e.target.value) : null)} />
            <p className="text-xs text-muted-foreground">Leave blank to disable. Email notifications are sent at multiples of this value, such as after 10, 20, and 30 submissions.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? <><RefreshCw className="h-4 w-4 animate-spin mr-1.5" />Creating...</> : 'Create Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Lists the keys a link's query string can fill, so a campaign link can carry e.g. ?utm_source=facebook. */
function PrefillHelp({ formId }: { formId: string }) {
  const { data: form } = useFormById(formId)
  const fields = (form?.fields ?? []).filter(isDataField).filter(f => f.type !== 'FILE_UPLOAD' && f.type !== 'EMAIL_MESSAGE')
  if (fields.length === 0) return null
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 space-y-2">
      <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Prefill from the link</p>
      <p className="text-xs text-neutral-500">
        Add <code className="font-mono">?field_key=value</code> to a public link to fill a field. Fields hidden on the public form are still filled this way.
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {fields.map(f => (
          <li key={f.id} className="rounded-md border border-neutral-200 bg-white px-2 py-0.5 font-mono text-[11px] text-neutral-600" title={f.label}>
            {f.key}{isHiddenOnPublicForm(f) && <span className="ml-1 font-sans text-neutral-400">(hidden)</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── ShareTab ──────────────────────────────────────────────────────────────────

export function ShareTab({ formId, recordGroupId }: ShareTabProps) {
  const [createOpen, setCreateOpen] = useState(false)
  const { links, isLoading, create, isCreating, toggle, isToggling, remove, isDeleting, update } = useFormPublicLinks(formId)
  // Public-link permissions are granted per record group.
  const groupActions = useAllowedActions('RECORD_GROUP', recordGroupId)
  const canManage = groupActions.can(PERMISSION_KEYS.FORM_PUBLIC_LINK_MANAGE)
  const canCreate = canManage
  const canView   = groupActions.can(PERMISSION_KEYS.FORM_PUBLIC_LINK_VIEW)
  const canDelete = canManage

  // Fetch all form versions so the user can pick a different version for a link
  const { data: allForms = [] } = useAllForms(recordGroupId)

  if (!canView) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">You don't have permission to view public links.</p>
      </div>
    )
  }

  const handleCreate = async (data: CreatePublicLinkRequest) => {
    try {
      await create(data)
      setCreateOpen(false)
      toast.success('Public link created')
    } catch {
      toast.error('Failed to create link')
    }
  }

  const handleToggle = async (linkId: string) => {
    try {
      await toggle(linkId)
    } catch {
      toast.error('Failed to toggle link')
    }
  }

  const handleDelete = async (linkId: string) => {
    try {
      await remove(linkId)
      toast.success('Link deleted')
    } catch {
      toast.error('Failed to delete link')
    }
  }

  const handleChangeVersion = async (linkId: string, newFormId: string) => {
    try {
      await update({ linkId, data: { formId: newFormId } })
      toast.success('Form version updated')
    } catch {
      toast.error('Failed to update form version')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Public Links</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Share these links to let external users submit records without an account.
          </p>
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />New Link
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : links.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border-2 border-dashed border-border bg-muted/30">
          <div className="h-12 w-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
            <ExternalLink className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold text-muted-foreground">No public links yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs">
            Create a link to let external users submit responses to this form.
          </p>
          {canCreate && (
            <Button size="sm" className="mt-4 gap-1.5" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />Create First Link
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {links.map(link => (
            <LinkCard
              key={link.id} link={link}
              allForms={allForms}
              onToggle={() => handleToggle(link.id)}
              onDelete={() => handleDelete(link.id)}
              onChangeVersion={(newFormId) => handleChangeVersion(link.id, newFormId)}
              isToggling={isToggling}
              isDeleting={isDeleting}
              canManage={canManage}
              canDelete={canDelete}
            />
          ))}
        </div>
      )}

      {links.length > 0 && <PrefillHelp formId={formId} />}

      <CreateLinkDialog
        formId={formId} open={createOpen} onClose={() => setCreateOpen(false)}
        onCreate={handleCreate} isCreating={isCreating} allForms={allForms}
      />
    </div>
  )
}
